import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import useTracking from '@/hooks/useTracking';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ArrowLeft, ShoppingCart, Package, Star, Check, Minus, Plus, Sparkles, Heart, Users, Target, Loader2, ExternalLink, QrCode, Building, Wallet, Landmark, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { getProductImageUrl, getProductImages, resolveMediaUrl } from '@/lib/productMedia';

export default function ProductDetail() {
  const { productId } = useParams();
  const subdomain = useStoreSubdomain();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [cart, setCart] = useState([]);
  const [resellerPrice, setResellerPrice] = useState(null);
  const [bundleSuggestions, setBundleSuggestions] = useState([]);
  const { trackEvent } = useTracking(store?.store_extras);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [storeRes, productRes] = await Promise.all([
          api.get(`/store/${subdomain}`),
          api.get(`/store/${subdomain}/products/${productId}`)
        ]);
        setStore(storeRes.data);
        setProduct(productRes.data);
        if (productRes.data.name) document.title = `${productRes.data.name} — ${storeRes.data?.name || 'Toko Online'}`;
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const savedCart = localStorage.getItem(`cart_${subdomain}`);
    if (savedCart) setCart(JSON.parse(savedCart));
  }, [subdomain, productId]);

  // Fetch reseller price if logged in
  useEffect(() => {
    if (product && localStorage.getItem('token')) {
      api.get(`/store/${subdomain}/reseller-price/${productId}`)
        .then(res => setResellerPrice(res.data))
        .catch(() => {});
    }
  }, [product, subdomain, productId]);

  // Fetch bundle suggestions for this product
  useEffect(() => {
    if (product) {
      api.get(`/store/${subdomain}/bundle-suggestions?product_ids=${productId}`)
        .then(res => setBundleSuggestions(res.data.bundles || []))
        .catch(() => {});
    }
  }, [product, subdomain, productId]);

  // Donation state
  const [donationInfo, setDonationInfo] = useState(null);
  const [donationForm, setDonationForm] = useState({
    donor_name: '', donor_email: '', donor_phone: '', amount: 0, message: '', is_anonymous: false, payment_method: 'qris'
  });
  const [donationSubmitting, setDonationSubmitting] = useState(false);

  // High-ticket state
  const [htPaymentType, setHtPaymentType] = useState('booking'); // booking, dp, full

  // Fetch donation info if type is donation
  useEffect(() => {
    if (product && product.type === 'donation') {
      api.get(`/store/${subdomain}/products/${productId}/donation-info`)
        .then(res => setDonationInfo(res.data))
        .catch(() => {});
    }
  }, [product, subdomain, productId]);

  const handleDonate = async () => {
    if (!donationForm.donor_name || !donationForm.donor_email || !donationForm.amount) {
      toast.error('Lengkapi nama, email, dan nominal donasi');
      return;
    }
    if (donationForm.amount < 1000) {
      toast.error('Minimum donasi Rp 1.000');
      return;
    }
    setDonationSubmitting(true);
    try {
      const res = await api.post(`/store/${subdomain}/product-donate`, {
        product_id: product.id,
        ...donationForm
      });
      if (res.data.payment_url && donationForm.payment_method !== 'bank_transfer') {
        window.open(res.data.payment_url, '_blank');
      }
      const donId = res.data.donation?.id || res.data.donation_id;
      if (donId) {
        navigate(`/store/${subdomain}/thank-you?donation=${donId}&type=donation`);
      } else {
        toast.success('Donasi berhasil dikirim!');
      }
      setDonationForm({ donor_name: '', donor_email: '', donor_phone: '', amount: 0, message: '', is_anonymous: false, payment_method: 'qris' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memproses donasi');
    } finally {
      setDonationSubmitting(false);
    }
  };

  // Fire ViewContent when product loads
  useEffect(() => {
    if (product) {
      trackEvent('ViewContent', { id: product.id, name: product.name, price: product.sale_price || product.price });
    }
  }, [product, trackEvent]);

  const addToCart = () => {
    const existing = cart.find(c => c.product_id === product.id);
    let newCart;
    const cartItem = {
      product_id: product.id,
      name: product.name,
      price: product.sale_price || product.price,
      quantity: product.is_high_ticket ? 1 : quantity,
      image: getProductImageUrl(product),
      type: product.type,
      is_high_ticket: product.is_high_ticket || false,
      ht_payment_type: product.is_high_ticket ? htPaymentType : undefined,
    };
    if (existing) {
      newCart = cart.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + (product.is_high_ticket ? 1 : quantity), ht_payment_type: cartItem.ht_payment_type } : c);
    } else {
      newCart = [...cart, cartItem];
    }
    setCart(newCart);
    localStorage.setItem(`cart_${subdomain}`, JSON.stringify(newCart));
    trackEvent('AddToCart', { id: product.id, name: product.name, price: product.sale_price || product.price, quantity });
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const primaryColor = store?.primary_color || '#2D6CDF';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" />
    </div>
  );

  if (!product) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Package size={48} className="mx-auto text-slate-300 mb-4" />
        <h1 className="text-xl font-bold text-slate-900">Produk Tidak Ditemukan</h1>
        <Link to={`/store/${subdomain}`} className="text-[#2D6CDF] hover:underline mt-2 inline-block">Kembali ke Toko</Link>
      </div>
    </div>
  );

  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const discountPercent = hasDiscount ? Math.round((1 - product.sale_price / product.price) * 100) : 0;
  const cartCount = cart.reduce((a, c) => a + c.quantity, 0);

  const presetAmounts = [10000, 25000, 50000, 100000, 250000, 500000];

  // =================== DONATION PRODUCT VIEW ===================
  if (product.type === 'donation') {
    const di = donationInfo || {};
    const totalRaised = di.total_raised || 0;
    const donorCount = di.donor_count || 0;
    const donationTarget = di.donation_target || product.donation_target || 0;
    const progressPercent = donationTarget > 0 ? Math.min(100, Math.round((totalRaised / donationTarget) * 100)) : 0;
    const recentDonors = di.recent_donors || [];

    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50" data-testid="product-detail-page">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-sm border-b border-pink-100 sticky top-0 z-50">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft size={20} />
              <span className="font-medium">{store?.name}</span>
            </Link>
            <Badge className="bg-pink-500 text-white border-0 gap-1.5">
              <Heart size={14} /> Donasi
            </Badge>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Campaign Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Hero Card */}
              <Card className="border-0 shadow-xl overflow-hidden rounded-2xl">
                <div className="h-56 relative overflow-hidden">
                  {getProductImageUrl(product) ? (
                    <img src={getProductImageUrl(product)} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-pink-400 via-rose-500 to-orange-400 flex items-center justify-center">
                      <Heart size={80} className="text-white/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                      {product.name}
                    </h1>
                  </div>
                </div>

                <CardContent className="p-6">
                  {/* Progress Section */}
                  <div className="mb-6">
                    <div className="flex justify-between items-end mb-3">
                      <div>
                        <p className="text-3xl font-bold text-pink-600">Rp {fmt(totalRaised)}</p>
                        <p className="text-sm text-slate-500 mt-1">terkumpul{donationTarget > 0 ? ` dari target Rp ${fmt(donationTarget)}` : ''}</p>
                      </div>
                      {donationTarget > 0 && (
                        <span className="text-2xl font-bold text-pink-600">{progressPercent}%</span>
                      )}
                    </div>
                    {donationTarget > 0 && (
                      <div className="h-4 bg-pink-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all duration-1000"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 py-4 border-t border-slate-100">
                    <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                        <Users size={20} className="text-pink-500" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-slate-900">{donorCount}</p>
                        <p className="text-xs text-slate-500">Donatur</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Target size={20} className="text-orange-500" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-slate-900">Rp {fmt(donationTarget)}</p>
                        <p className="text-xs text-slate-500">Target</p>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {product.description && (
                    <div className="pt-4 border-t border-slate-100">
                      <h3 className="font-semibold text-slate-900 mb-2">Tentang Program Donasi</h3>
                      <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">{product.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Donors */}
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Heart size={18} className="text-pink-500" /> Donatur Terbaru
                  </h3>
                  <div className="space-y-3">
                    {recentDonors.length > 0 ? recentDonors.map((d, i) => (
                      <div key={i} className="flex items-start gap-3 pb-3 border-b border-slate-50 last:border-0">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center flex-shrink-0">
                          <Heart size={14} className="text-pink-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900 truncate">{d.is_anonymous ? 'Hamba Allah' : d.donor_name}</p>
                            <p className="text-sm font-bold text-pink-600 flex-shrink-0">Rp {fmt(d.amount)}</p>
                          </div>
                          {d.message && <p className="text-xs text-slate-500 mt-1 line-clamp-2 italic">"{d.message}"</p>}
                          <p className="text-[11px] text-slate-400 mt-1">{d.created_at ? new Date(d.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</p>
                        </div>
                      </div>
                    )) : (
                      <div className="py-8 text-center">
                        <Heart size={32} className="mx-auto text-pink-200 mb-2" />
                        <p className="text-sm text-slate-400">Jadilah donatur pertama!</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Donation Form */}
            <div className="space-y-6">
              {/* Quick Donate */}
              <Card className="border-0 shadow-xl rounded-2xl sticky top-20">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-slate-900 mb-1 flex items-center gap-2">
                    <Heart size={20} className="text-pink-500" /> Berdonasi Sekarang
                  </h3>
                  <p className="text-sm text-slate-500 mb-5">Setiap donasi Anda sangat berarti</p>

                  {/* Preset Amounts */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {presetAmounts.map((amt) => (
                      <button
                        key={amt}
                        onClick={() => setDonationForm(f => ({ ...f, amount: amt }))}
                        className={`py-2.5 px-2 rounded-xl border-2 text-sm font-semibold transition-all ${
                          donationForm.amount === amt
                            ? 'border-pink-500 bg-pink-50 text-pink-600'
                            : 'border-slate-200 text-slate-600 hover:border-pink-200 hover:bg-pink-50/50'
                        }`}
                      >
                        {fmt(amt)}
                      </button>
                    ))}
                  </div>

                  {/* Custom Amount */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium text-slate-700">Nominal Lainnya (Rp)</Label>
                    <Input
                      type="number"
                      value={donationForm.amount || ''}
                      onChange={(e) => setDonationForm(f => ({ ...f, amount: parseInt(e.target.value) || 0 }))}
                      placeholder="Minimum Rp 1.000"
                      className="mt-1 text-lg font-semibold"
                    />
                  </div>

                  <div className="border-t border-slate-100 pt-4 space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Nama Lengkap *</Label>
                      <Input
                        value={donationForm.donor_name}
                        onChange={(e) => setDonationForm(f => ({ ...f, donor_name: e.target.value }))}
                        placeholder="Nama Anda"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Email *</Label>
                      <Input
                        type="email"
                        value={donationForm.donor_email}
                        onChange={(e) => setDonationForm(f => ({ ...f, donor_email: e.target.value }))}
                        placeholder="email@contoh.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">No. WhatsApp</Label>
                      <Input
                        value={donationForm.donor_phone}
                        onChange={(e) => setDonationForm(f => ({ ...f, donor_phone: e.target.value }))}
                        placeholder="08xxxxxxxxxx"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Pesan / Doa</Label>
                      <Textarea
                        value={donationForm.message}
                        onChange={(e) => setDonationForm(f => ({ ...f, message: e.target.value }))}
                        placeholder="Semoga bermanfaat..."
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <Label className="text-sm text-slate-600">Sembunyikan nama (Hamba Allah)</Label>
                      <Switch
                        checked={donationForm.is_anonymous}
                        onCheckedChange={(v) => setDonationForm(f => ({ ...f, is_anonymous: v }))}
                      />
                    </div>

                    {/* Payment Method */}
                    <div>
                      <Label className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-2">
                        <CreditCard size={16} className="text-pink-500" /> Metode Pembayaran
                      </Label>
                      <RadioGroup
                        value={donationForm.payment_method}
                        onValueChange={(v) => setDonationForm(f => ({ ...f, payment_method: v }))}
                        className="grid grid-cols-2 gap-2"
                      >
                        <label className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${
                          donationForm.payment_method === 'qris' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                        }`}>
                          <RadioGroupItem value="qris" />
                          <QrCode size={16} />
                          <span className="font-medium">QRIS</span>
                        </label>
                        <label className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${
                          donationForm.payment_method === 'va' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                        }`}>
                          <RadioGroupItem value="va" />
                          <Building size={16} />
                          <span className="font-medium">Virtual Account</span>
                        </label>
                        <label className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${
                          donationForm.payment_method === 'ewallet' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                        }`}>
                          <RadioGroupItem value="ewallet" />
                          <Wallet size={16} />
                          <span className="font-medium">E-Wallet</span>
                        </label>
                        <label className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all text-sm ${
                          donationForm.payment_method === 'bank_transfer' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-slate-200 hover:border-pink-200'
                        }`}>
                          <RadioGroupItem value="bank_transfer" />
                          <Landmark size={16} />
                          <span className="font-medium">Transfer Bank</span>
                        </label>
                      </RadioGroup>
                    </div>
                  </div>

                  <Button
                    onClick={handleDonate}
                    disabled={donationSubmitting}
                    className="w-full mt-4 h-12 text-base font-bold rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-lg shadow-pink-200"
                  >
                    {donationSubmitting ? (
                      <Loader2 size={18} className="animate-spin mr-2" />
                    ) : (
                      <Heart size={18} className="mr-2" />
                    )}
                    Donasi Rp {fmt(donationForm.amount)}
                  </Button>

                  <p className="text-[11px] text-slate-400 text-center mt-3">
                    Pembayaran aman & terenkripsi
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const productImages = getProductImages(product);
  const mainImageUrl = productImages[selectedImage] ? resolveMediaUrl(productImages[selectedImage]) : getProductImageUrl(product);

  // =================== REGULAR PRODUCT VIEW ===================
  return (
    <div className="min-h-screen bg-slate-50" data-testid="product-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20} />
            <span className="font-medium">{store?.name}</span>
          </Link>
          <Link to={`/store/${subdomain}/checkout`}>
            <Button className="relative" style={{ backgroundColor: primaryColor }} data-testid="cart-btn">
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">{cartCount}</span>
              )}
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            <div className="aspect-square bg-white rounded-lg border border-slate-200 overflow-hidden">
              {mainImageUrl ? (
                <img src={mainImageUrl} alt={product.name} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package size={64} className="text-slate-300" />
                </div>
              )}
            </div>
            {productImages.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto">
                {productImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className={`w-16 h-16 rounded border-2 overflow-hidden flex-shrink-0 ${selectedImage === i ? 'border-[#2D6CDF]' : 'border-slate-200'}`}
                  >
                    <img src={resolveMediaUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge style={{ backgroundColor: primaryColor }}>{product.type}</Badge>
              {product.category && <Badge variant="outline">{product.category}</Badge>}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>{product.name}</h1>
            
            {/* Price */}
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl font-bold" style={{ color: primaryColor }}>Rp {fmt(product.sale_price || product.price)}</span>
              {hasDiscount && (
                <>
                  <span className="text-lg text-slate-400 line-through">Rp {fmt(product.price)}</span>
                  <Badge className="bg-red-500">-{discountPercent}%</Badge>
                </>
              )}
            </div>

            {/* Reseller Price */}
            {resellerPrice && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-600">Harga Reseller</Badge>
                  <span className="text-lg font-bold text-green-700">Rp {fmt(resellerPrice.reseller_price)}</span>
                  <span className="text-sm text-green-600">Margin: Rp {fmt(resellerPrice.margin)}</span>
                </div>
                <p className="text-xs text-green-600 mt-1">Tier: {resellerPrice.tier_name} — Gunakan kode reseller saat checkout</p>
              </div>
            )}

            {/* Description */}
            {product.description && (
              <div className="mb-6">
                <h3 className="font-medium text-slate-900 mb-2">Deskripsi</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{product.description}</p>
              </div>
            )}

            {/* Stock */}
            {product.type === 'physical' && (
              <div className="mb-4">
                <span className={`text-sm ${product.stock > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {product.stock > 0 ? `Stok: ${product.stock}` : 'Stok Habis'}
                </span>
              </div>
            )}

            {/* Payment Plans (non high-ticket) */}
            {!product.is_high_ticket && product.payment_plans?.length > 0 && (
              <Card className="border-slate-200 mb-4">
                <CardContent className="p-4">
                  <h3 className="font-medium text-slate-900 mb-3">Opsi Pembayaran</h3>
                  <div className="space-y-2">
                    {product.payment_plans.map(plan => (
                      <div key={plan.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{plan.name}</p>
                          <p className="text-xs text-slate-500">DP {fmt(plan.dp_amount)} + {plan.num_installments}x cicilan</p>
                        </div>
                        <Badge variant="outline">Tersedia</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* High-Ticket Installment Options */}
            {product.is_high_ticket && (() => {
              const htPrice = product.sale_price || product.price;
              const bookingFee = product.booking_fee_type === 'percentage'
                ? Math.round(htPrice * (product.booking_fee_value || 10) / 100)
                : (product.booking_fee_value || 0);
              const dpAmount = Math.round(htPrice * (product.dp_percentage || 30) / 100);
              const remainingAfterDp = htPrice - dpAmount;
              const numInstallments = product.installment_count || 3;
              const installmentAmount = numInstallments > 0 ? Math.round(remainingAfterDp / numInstallments) : 0;

              return (
                <Card className="border-blue-200 bg-blue-50/50 mb-4">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <CreditCard size={18} className="text-blue-600" /> Skema Pembayaran Termin
                    </h3>

                    {/* Payment breakdown summary */}
                    <div className="bg-white rounded-lg p-3 mb-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Harga Total</span>
                        <span className="font-bold">Rp {fmt(htPrice)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Booking Fee ({product.booking_fee_type === 'percentage' ? `${product.booking_fee_value || 10}%` : 'fixed'})</span>
                        <span className="font-medium text-blue-600">Rp {fmt(bookingFee)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Down Payment ({product.dp_percentage || 30}%)</span>
                        <span className="font-medium text-blue-600">Rp {fmt(dpAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Sisa setelah DP</span>
                        <span>Rp {fmt(remainingAfterDp)}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-100 pt-2">
                        <span className="text-slate-600">Cicilan ({numInstallments}x)</span>
                        <span className="font-medium">Rp {fmt(installmentAmount)} / bulan</span>
                      </div>
                    </div>

                    {/* Payment type selection */}
                    <p className="text-sm font-medium text-slate-700 mb-2">Pilih Tipe Pembayaran:</p>
                    <RadioGroup value={htPaymentType} onValueChange={setHtPaymentType} className="space-y-2">
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${htPaymentType === 'booking' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-200'}`}>
                        <RadioGroupItem value="booking" id="ht-booking" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Booking Fee</p>
                          <p className="text-xs text-slate-500">Bayar booking fee dulu, lanjut DP + cicilan</p>
                        </div>
                        <span className="font-bold text-blue-600 text-sm">Rp {fmt(bookingFee)}</span>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${htPaymentType === 'dp' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-200'}`}>
                        <RadioGroupItem value="dp" id="ht-dp" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Down Payment</p>
                          <p className="text-xs text-slate-500">Bayar DP langsung, lanjut cicilan bulanan</p>
                        </div>
                        <span className="font-bold text-blue-600 text-sm">Rp {fmt(dpAmount)}</span>
                      </label>
                      <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${htPaymentType === 'full' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-200'}`}>
                        <RadioGroupItem value="full" id="ht-full" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Bayar Lunas</p>
                          <p className="text-xs text-slate-500">Bayar penuh tanpa cicilan</p>
                        </div>
                        <span className="font-bold text-blue-600 text-sm">Rp {fmt(htPrice)}</span>
                      </label>
                    </RadioGroup>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4">
              {!product.is_high_ticket && (
                <div className="flex items-center border border-slate-200 rounded-lg">
                  <Button variant="ghost" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} data-testid="decrease-qty">
                    <Minus size={16} />
                  </Button>
                  <span className="w-12 text-center font-medium">{quantity}</span>
                  <Button variant="ghost" size="icon" onClick={() => setQuantity(quantity + 1)} data-testid="increase-qty">
                    <Plus size={16} />
                  </Button>
                </div>
              )}
              <Button 
                onClick={addToCart} 
                className="flex-1"
                style={{ backgroundColor: primaryColor }}
                disabled={product.type === 'physical' && product.stock <= 0}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart size={18} className="mr-2" /> Tambah ke Keranjang
              </Button>
            </div>

            {/* Features */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-emerald-500" /> Garansi Toko
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-emerald-500" /> Pembayaran Aman
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-emerald-500" /> Pengiriman Cepat
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check size={16} className="text-emerald-500" /> Support 24/7
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bundle Suggestions */}
        {bundleSuggestions.length > 0 && (
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles size={20} className="text-purple-500" />
              <h2 className="text-xl font-bold text-slate-900">Hemat Lebih Banyak dengan Bundle</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {bundleSuggestions.map(b => {
                const savings = b.original_total && b.final_price ? Math.round((1 - b.final_price / b.original_total) * 100) : 0;
                return (
                  <Link key={b.id} to={`/store/${subdomain}/bundle/${b.id}`}>
                    <Card className="border border-purple-100 hover:border-purple-300 hover:shadow-md transition-all">
                      <CardContent className="p-4 flex gap-4">
                        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-purple-50 flex items-center justify-center">
                          {b.images?.[0] ? (
                            <img src={b.images[0]} alt={b.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package size={24} className="text-purple-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">Bundle</Badge>
                            {savings > 0 && <Badge className="bg-red-100 text-red-700 border-0 text-xs">Hemat {savings}%</Badge>}
                          </div>
                          <h3 className="font-semibold text-slate-900 text-sm line-clamp-1">{b.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {b.original_total > b.final_price && (
                              <span className="text-xs text-slate-400 line-through">Rp {b.original_total.toLocaleString('id-ID')}</span>
                            )}
                            <span className="text-sm font-bold text-purple-600">Rp {b.final_price?.toLocaleString('id-ID')}</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{b.items?.length || 0} produk dalam paket</p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
